import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import type { AdAdapter, AdFormat } from './ads';
import { adFormats, type AdFormats } from './release-features';

export interface BannerState { enabled: boolean; status: 'hidden' | 'loading' | 'visible' | 'unavailable' }
export interface NativeAdBridge {
  prepare(): Promise<{ canRequestAds: boolean; testInventory: boolean; privacyRequired: boolean }>;
  show(options: { format: AdFormat }): Promise<{ earned: boolean; transactionId: string; unavailable?: boolean }>;
  privacy(): Promise<unknown>;
  pendingRewards(): Promise<{ transactionIds: string[] }>;
  acknowledge(options: { transactionId: string }): Promise<void>;
  banner(options: { enabled: boolean; visible: boolean; label: string }): Promise<BannerState>;
  bannerVisibility(options: { visible: boolean; label: string }): Promise<BannerState>;
  addListener(event: 'bannerState', listener: (state: BannerState) => void): Promise<PluginListenerHandle>;
}
export const nativeAdBridge = registerPlugin<NativeAdBridge>('WordHuliAds');

/** Overlay callbacks suspend interaction, motion and sound across native forms. */
export class NativeAds implements AdAdapter {
  readonly mode = 'native';
  private active = false;
  private bannerEnabled = false;
  constructor(private bridge: NativeAdBridge, private suspend: () => void, private resume: () => void, private formats: AdFormats = adFormats) {}

  async prepare(): Promise<boolean> {
    if (this.active) return false;
    this.active = true; this.suspend();
    try { return (await this.bridge.prepare()).canRequestAds; }
    catch { return false; }
    finally { this.active = false; this.resume(); }
  }

  async show(format: AdFormat): Promise<{ earned: boolean; transactionId: string }> {
    if (this.active || !this.formats[format]) return { earned: false, transactionId: '' };
    this.active = true; this.suspend();
    try {
      if (!(await this.bridge.prepare()).canRequestAds) return { earned: false, transactionId: '' };
      const result = await this.bridge.show({ format });
      return { earned: format === 'rewarded' && result.earned === true, transactionId: result.transactionId };
    } catch { return { earned: false, transactionId: '' }; }
    finally { this.active = false; this.resume(); }
  }
  async privacy(): Promise<void> {
    if (this.active) return;
    this.active = true; this.suspend();
    try { await this.bridge.prepare(); await this.bridge.privacy(); this.bannerEnabled = false; }
    finally { this.active = false; this.resume(); }
  }
  async banner(enabled: boolean, visible: boolean, label: string): Promise<BannerState> {
    if (enabled && !this.formats.banner) return { enabled: false, status: 'unavailable' };
    if (this.active) return { enabled: this.bannerEnabled, status: 'unavailable' };
    this.active = true; this.suspend();
    try {
      if (enabled && !(await this.bridge.prepare()).canRequestAds) {
        await this.bridge.banner({ enabled: false, visible: false, label });
        this.bannerEnabled = false;
        return { enabled: false, status: 'unavailable' };
      }
      const state = await this.bridge.banner({ enabled, visible, label });
      this.bannerEnabled = state.enabled; return state;
    } catch { return { enabled: this.bannerEnabled, status: 'unavailable' }; }
    finally { this.active = false; this.resume(); }
  }
  async recover(state: { grants: string[]; extraHints: number; game?: { status: string } | null }, save: () => Promise<boolean>): Promise<boolean> {
    const { transactionIds } = await this.bridge.pendingRewards();
    return settleNativeRewards(state, transactionIds, save, id => this.bridge.acknowledge({ transactionId: id }));
  }
}

/** A failed save leaves the native journal pending. Retry deduplicates in memory,
 * and a process restart deduplicates against the last committed snapshot. */
export async function settleNativeRewards(
  state: { grants: string[]; extraHints: number; game?: { status: string } | null }, transactionIds: string[],
  save: () => Promise<boolean>, acknowledge: (id: string) => Promise<void>,
): Promise<boolean> {
  if (!Array.isArray(transactionIds) || transactionIds.length > 64 || transactionIds.some(id => typeof id !== 'string' || !id || id.length > 128)) throw new Error('Invalid native reward journal');
  const ids = [...new Set(transactionIds)];
  if (!ids.length) return true;
  for (const id of ids) if (!state.grants.includes(id)) {
    state.grants.push(id);
    if (state.game?.status === 'playing') state.extraHints++;
  }
  if (!await save()) return false;
  for (const id of ids) await acknowledge(id);
  return true;
}
